import { UserDataRecord as UserDataHeader } from "../../api/user-data-service";
import { SearchService } from "@nucleus/ui-base";
import { ServiceHandler } from "@nucleus/ui-base";
import { PageResult } from "@nucleus/ui-base";
import { Page, QuerySortDirection } from "@nucleus/ui-base";
import { Injectable } from "@angular/core";
import { UserDataFilter } from "./user-data.filter";
import { FilterRequest } from "../filter-request";
import { UserData } from "../../api/user-data-service";
import { Observable } from "rxjs";
import {
  PaginatedFilterRequestUserDataFilter,
  UserDataControllerService,
  UserDataUpsertRequest,
  UserScope,
} from "../../api/user-data-service";
import { RequestHandler } from "../request-handler";


@Injectable({
  providedIn: 'root'
})
export class UserDataService implements SearchService<UserDataHeader, UserDataFilter> {

  constructor(
    private api: UserDataControllerService,
    private requestHandler: RequestHandler,
  ) { }


  getById(id: number, handler: ServiceHandler<UserData>): void {
    // OpenAPI Generator collided UserDataController.findById with
    // UserDataSourceController.findById and renamed ours to findById1.
    // TODO(P3.x): add @Operation(operationId="findUserDataById") to the
    // backend so the regen restores the canonical name.
    this.requestHandler.handleRequest(this.api.findById1(id) as unknown as Observable<UserData>, handler, false);
  }

  // Type+key lookup — preferred for callers that already hold the (type, key)
  // pair from a list row (every list-page row carries both), since id can be
  // absent on rows projected from the ES read path post-TASK-178 F5.
  getByTypeAndKey(type: string, key: string, handler: ServiceHandler<UserData>): void {
    this.requestHandler.handleRequest(this.api.findByTypeAndKey(type, key) as unknown as Observable<UserData>, handler, false);
  }


  search(handler: ServiceHandler<UserDataHeader[]>): void {
    this.requestHandler.handleRequest(this.api.findAll() as unknown as Observable<UserDataHeader[]>, handler, false);
  }

  getUserDataTypes(handler: ServiceHandler<String[]>) {
    this.requestHandler.handleRequest(this.api.findAllTypes(), handler, false);
  }

  /** Distinct non-null department values across all user_data rows.
   *  Drives the data-page department-filter autocomplete + the edit dialog. */
  getDepartments(handler: ServiceHandler<String[]>) {
    this.requestHandler.handleRequest(this.api.findAllDepartments(), handler, false);
  }

  /** Distinct non-null org-unit values across all user_data rows. */
  getOrgUnits(handler: ServiceHandler<String[]>) {
    this.requestHandler.handleRequest(this.api.findAllOrgUnits(), handler, false);
  }

  /** Distinct non-null financial-unit values across all user_data rows. */
  getFinancialUnits(handler: ServiceHandler<String[]>) {
    this.requestHandler.handleRequest(this.api.findAllFinancialUnits(), handler, false);
  }

  /**
   * UF2 (TASK-178). Returns the caller's authorized org-scope codes
   * — departments / orgUnits / financialUnits arrays plus an
   * {@link UserScope#allUnits} bypass flag for the sysadmin role.
   * The server clamps to the JWT `units` claim resolved through the
   * org_unit table; results are NOT the global catalog distinct from
   * all rows. Drives the filter sidebar + the edit-dialog pickers.
   */
  getFilterOptions(handler: ServiceHandler<UserScope>) {
    this.requestHandler.handleRequest(this.api.filterOptions(), handler, false);
  }


  searchPaginated(handler: ServiceHandler<PageResult<UserDataHeader>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection) {
  }

  filterPaginated(handler: ServiceHandler<PageResult<UserDataHeader>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection, filter: UserDataFilter): void {
    let request = new FilterRequest<UserDataFilter>(filter);
    request.page.pageSize = pageSize;
    request.page.pageNumber = pageNumber;
    request.page.sortDirection = sortDirection;
    request.page.sortField = softField;
    this.filter(handler, request.filter, request.page);
  }

  filter(handler: ServiceHandler<PageResult<UserDataHeader>>, filter: UserDataFilter, page?: Page, _clientId?: string): void {
    const request = new FilterRequest<UserDataFilter>(filter);
    if (page) { request.page = page; }
    this.requestHandler.handleRequest(
      this.api.filter(this.toApiFilterRequest(request)) as unknown as Observable<PageResult<UserDataHeader>>,
      handler,
      false,
    );
  }

  private toApiFilterRequest(request: FilterRequest<UserDataFilter>): PaginatedFilterRequestUserDataFilter {
    return {
      page: {
        pageSize: request.page?.pageSize,
        pageNumber: request.page?.pageNumber,
        sortField: request.page?.sortField,
        sortDirection: request.page?.sortDirection as unknown as string,
      },
      filter: { ...(request.filter as any) },
    };
  }


  purge(handler: ServiceHandler<void>): void {
    this.requestHandler.handleRequest(this.api.purge(), handler, true);
  }

  purgeByType(type: string, handler: ServiceHandler<void>): void {
    this.requestHandler.handleRequest(this.api.purge1(type), handler, true);
  }

  /** POST /userdata — idempotent upsert by (type, key). Used for create. */
  upsert(req: UserDataUpsertRequest, handler: ServiceHandler<UserData>): void {
    this.requestHandler.handleRequest(this.api.upsert(req), handler, true);
  }

  /** PUT /userdata/{type}/{key} — same merge semantics, path-addressed.
   *  Use for edit when the user can't change the (type, key) pair. */
  put(type: string, key: string, req: UserDataUpsertRequest, handler: ServiceHandler<UserData>): void {
    this.requestHandler.handleRequest(this.api.put(type, key, req), handler, true);
  }

  /** DELETE /userdata/{type}/{key}. */
  deleteByTypeAndKey(type: string, key: string, handler: ServiceHandler<void>): void {
    this.requestHandler.handleRequest(this.api._delete(type, key), handler, true);
  }

  /** Admin reset of a burn-down row's capacity_remaining. POSTs to
   *  /userdata/{id}/reset-capacity via the userdata-service OpenAPI client. */
  resetCapacity(id: number, handler: ServiceHandler<UserData>): void {
    this.requestHandler.handleRequest(this.api.resetCapacity(id), handler, true);
  }
}
